import { memo, useMemo } from 'react';
import classnames from 'classnames';
import Select from 'react-select';
import { getFlagURL } from '@scramble/utils';
import { SingleValueCountry as SingleValue } from './value';
import { OptionCountry as Option } from './option';
import type { CountryOption, CountrySelectProps } from './types';
import s from './index.module.scss';

export const CountrySelect = memo(function CountrySelect({
  selected,
  label,
  name = 'country',
  isRequired = false,
  options = [],
  onChange,
  error,
  className,
  ...props
}: CountrySelectProps) {
  const countriesMap = useMemo<Record<string, CountryOption>>(() => {
    if (!options.length) return {};

    return options.reduce<Record<string, CountryOption>>((map, country) => {
      map[country.code] = {
        label: country.name ?? 'Unknown',
        value: country.code,
        flag: getFlagURL(country.code),
        ...country,
      };
      return map;
    }, {});
  }, [options]);

  const countries = useMemo(() => Object.values(countriesMap), [countriesMap]);

  return (
    <div className={classnames(s.wrapper, className)}>
      {label && (
        <label htmlFor={name} className={s.label}>
          <span className="f-body-sm f-500">{label}</span>
          {isRequired && (
            <span className={classnames(s.required, error && s.required_error)}>*</span>
          )}
        </label>
      )}
      <Select
        value={selected}
        onChange={onChange}
        name={name}
        classNamePrefix="country_select"
        className={classnames(s.country_select, error && s.country_select_error)}
        options={countries}
        components={{ Option, SingleValue }}
        placeholder=""
        menuPlacement="auto"
        isSearchable
        aria-errormessage={error}
        aria-label={label}
        {...props}
      />
      {error && <div className={s.error_message}>{error}</div>}
    </div>
  );
});